import * as React from 'react';
import { type Color } from '@chess/api-model';
import { RANKED_MIN_GAMES, type OpeningRow, type OpeningsRanking } from '@chess/api-insights';
import { FilterAnchor, signed, type FilterLink } from '@chess/api-insights/charts';
import { CardEmpty, useDashboardParts } from '@chess/api-dashboard/react';
import { repertoireOf, type RepertoireFamily } from './repertoire.ts';
import './openings-performance.css';

// The openings screen's repertoire, presentational (2026-10-09; the As White
// and As Black tables by variation, and the More section's Most played and
// Winning openings, before): one card per colour, each family a row with its
// games and average score after the opening, most played first, opening into its
// variations; families with too few scanned games collapsed under them.
// Every name is a link to the library filtered to it and its colour. A
// composer: it draws its two cards and their section with the dashboard's
// parts (`useDashboardParts`, real inside the History section's `after`).

export interface OpeningsPerformanceProps {
  /** Undefined while the library's games are being read. */
  ranking?: OpeningsRanking;
  link?: FilterLink;
}

const COLOURS: Color[] = ['white', 'black'];
const TITLE: Record<Color, string> = { white: 'Your repertoire as White', black: 'Your repertoire as Black' };
const plural = (n: number, one: string, many = `${one}s`) =>
  `${n} ${n === 1 ? one : many}`;

const scoreClass = (score?: number) => `openings-col-num openings-score openings-score-${score === undefined ? 'none' : score >= 0 ? 'good' : 'bad'}`;
const scoreText = (score?: number) => (score === undefined ? '—' : signed(score));

/** A variation under its family: its name (the part after the family), games and score. */
const VariationRow: React.FC<{ row: OpeningRow; most: number; link?: FilterLink }> = ({ row, most, link }) => {
  const at = row.opening.indexOf(': ');
  const name = at < 0 ? 'Main line' : row.opening.slice(at + 2);
  return (
    <div className='openings-row openings-row-variation' role='row' title={`${row.opening} — ${plural(row.games, 'game')}${row.averageScore === undefined ? '' : `, ${signed(row.averageScore)} after the opening on average over ${row.scanned} scanned`}`}>
      <FilterAnchor className='openings-row-name' link={link} filter={{ variation: row.opening, colour: row.colour }}>
        {name}
      </FilterAnchor>
      <span className='openings-games-track' role='cell' aria-hidden='true'>
        <span className='openings-games-bar' style={{ width: `${(100 * row.games) / most}%` }} />
      </span>
      <span className='openings-col-num' role='cell'>{row.games}</span>
      <span className={scoreClass(row.averageScore)} role='cell'>{scoreText(row.averageScore)}</span>
    </div>
  );
};

/**
 * A family as one row (name, a bar for its games, the count, the average
 * score after the opening), opening on a click into its variations. The name
 * is a link to the library filtered to the family and colour.
 */
const FamilyRow: React.FC<{ family: RepertoireFamily; most: number; link?: FilterLink }> = ({ family, most, link }) => (
  <details className='openings-family'>
    <summary className='openings-row' role='row' title={`${family.family} — ${plural(family.games, 'game')}${family.averageScore === undefined ? '' : `, ${signed(family.averageScore)} after the opening on average over ${family.scanned} scanned`} · open for its variations`}>
      <span className='openings-family-name'>
        <span className='openings-caret' aria-hidden='true'>▸</span>
        <FilterAnchor className='openings-row-name' link={link} filter={{ opening: family.family, colour: family.colour }}>
          {family.family}
        </FilterAnchor>
      </span>
      <span className='openings-games-track' role='cell' aria-hidden='true'>
        <span className='openings-games-bar' style={{ width: `${(100 * family.games) / most}%` }} />
      </span>
      <span className='openings-col-num' role='cell'>{family.games}</span>
      <span className={scoreClass(family.averageScore)} role='cell'>{scoreText(family.averageScore)}</span>
    </summary>
    <div className='openings-variations'>
      {family.variations.map((v) => (
        <VariationRow key={v.opening} row={v} most={most} link={link} />
      ))}
    </div>
  </details>
);

const Head: React.FC = () => (
  <div className='openings-row openings-row-head' role='row'>
    <span role='columnheader'>Opening</span>
    <span role='columnheader' className='openings-col-games'>Games</span>
    <span role='columnheader' className='openings-col-num' />
    <span role='columnheader' className='openings-col-num' title='Your average engine score when the opening ended'>Score</span>
  </div>
);

/**
 * One colour's repertoire in a card: the ranked families most played first,
 * then, collapsed, those with too few scanned games. Its content fills the
 * card's body from the top (`.openings-colour`), an exception to the frame's
 * centred body, so the two colours' rows line up.
 */
const ColourSection: React.FC<{ colour: Color; ranking: OpeningsRanking; link?: FilterLink }> = ({ colour, ranking, link }) => {
  const { Card } = useDashboardParts();
  const { ranked, rest, games } = repertoireOf(ranking, colour);
  if (!games) return null;
  const most = Math.max(1, ...ranked.map((f) => f.games), ...rest.map((f) => f.games));
  return (
    <Card title={TITLE[colour]} aside='most played first' note={`${plural(games, 'game')} · a family opens its variations; a name opens its games`}>
      <div className='openings-colour'>
        {ranked.length ? (
          <div className='openings-rows' role='table' aria-label={`${TITLE[colour]}, most played first`}>
            <Head />
            {ranked.map((f) => (
              <FamilyRow key={f.family} family={f} most={most} link={link} />
            ))}
          </div>
        ) : (
          <CardEmpty>No opening has {RANKED_MIN_GAMES} scanned games yet.</CardEmpty>
        )}
        {rest.length > 0 && (
          <details className='openings-group'>
            <summary className='openings-group-title'>Too few scanned games ({rest.length})</summary>
            <div className='openings-rows' role='table'>
              {rest.map((f) => (
                <FamilyRow key={f.family} family={f} most={most} link={link} />
              ))}
            </div>
          </details>
        )}
      </div>
    </Card>
  );
};

/** Why some openings have no rank yet: a muted line under the tables. The scan itself is automatic. */
const Waiting: React.FC<{ waiting: number; openings: number }> = ({ waiting, openings }) =>
  waiting ? (
    <p className='openings-waiting'>
      {plural(openings, 'opening')} {openings === 1 ? 'needs' : 'need'} a scan to be ranked: {plural(waiting, 'game')}.
    </p>
  ) : null;

export const OpeningsPerformance: React.FC<OpeningsPerformanceProps> = ({
  ranking,
  link,
}) => {
  const { Section } = useDashboardParts();
  if (!ranking) return <p className='dash-empty viz'>Reading the library…</p>;
  const empty =
    !ranking.scored.length &&
    !ranking.unscanned.length &&
    !ranking.tooFew.length;
  return (
    <div className='openings-performance viz'>
      {empty ? (
        <p className='dash-empty'>
          No game of the last 30 days names its opening. Import games from the library.
        </p>
      ) : (
        <Section maxColumns={2}>
          {COLOURS.map((c) => (
            <ColourSection key={c} colour={c} ranking={ranking} link={link} />
          ))}
        </Section>
      )}
      <Waiting waiting={ranking.unscannedGames.length} openings={ranking.unscanned.length} />
    </div>
  );
};
